// Copyright (c) 2021 WiseTime. All rights reserved.

import React, { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Trans } from "@lingui/react"
import styled from "styled-components/macro"
import { darken, transparentize } from "polished"
import DatePicker from "react-datepicker"
import { useHistory } from "react-router-dom"
import "react-datepicker/dist/react-datepicker.css"
import { getDatepickerLocale } from "locales/i18nUtil"
import * as TimelineSlice from "../slices/timeline"
import {
  computeTotalPostedSecs,
  convertDateToTimelineDate,
  convertTimelineDateToTimelinePathDate,
  toDate,
  TODAY_DATE_PARAM,
} from "features/timeline/dateTime"
import CustomCalendarInput from "./CustomCalendarInput"
import { ROUTE_PATH } from "app/routing"
import * as TimelineStatsSlice from "../slices/stats"

const POSTED_DAY_CLASSNAME = "tl-react-datepicker__day--is-posted"
const UNPOSTED_DAY_CLASSNAME = "tl-react-datepicker__day--is-unposted"

const Wrapper = styled.div`
  @media (max-width: ${({ theme }): string => theme.screen.sm}) {
    order: 99;
  }

  .tl-calendar-popup {
    font-family: ${({ theme }): string => theme.fontFamily};
    font-size: 1.4rem;
    touch-action: manipulation;
    background: ${({ theme }): string => theme.colors.white};
    box-shadow: 1px 0 0 ${({ theme }): string => theme.colors.mercury},
                -1px 0 0 ${({ theme }): string => theme.colors.mercury},
                0 1px 0 ${({ theme }): string => theme.colors.mercury},
                0 -1px 0 ${({ theme }): string => theme.colors.mercury},
                0 3px 13px rgba(0, 0, 0, .08);
    border-radius: 5px 5px 5px 5px;
    border: none;
    margin-top: -8px;
  }

  .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle {
    margin-left: -4px;
    border-bottom-color: ${({ theme }): string => theme.colors.white};

    &:before {
      border-bottom-color: ${({ theme }): string => theme.colors.mercury};
    }
  }

  .react-datepicker__header {
    background-color: ${({ theme }): string => theme.colors.white};
    border-color: transparent;
    border-radius: 5px;
    padding: 20px 0 0;

    &:not(.react-datepicker__header--has-time-select) {
      border-radius: 5px;
    }

    @media (max-width: ${({ theme }): string => theme.screen.sm}) {
      padding: 15px 0 0;
    }
  }
  
  .react-datepicker__navigation {
    width: 38px;
    height: 38px;
    border: none;
    top: 15px;
    margin: 0;

    @media (max-width: ${({ theme }): string => theme.screen.sm}) {
      top: 10px;
    }

    &:after {
      content: "";
      border-color: rgba(0, 0, 0, .9);
      border-width: 1px 1px 0 0;
      border-style: solid;
      position: absolute;
      top: 14px;
      width: 10px;
      height: 10px;
      transition: border-color .3s ease;
    }

    &.react-datepicker__navigation--previous {
      left: 10px;

      &:after {
        left: 17px;
        transform: rotate(-140deg);
      }
    }

    &.react-datepicker__navigation--next {
      right: 10px;

      &:after {
        right: 17px;
        transform: rotate(45deg);
      }
    }

    &:hover {
      &:after {
        border-color: ${({ theme }): string => theme.colors.indigo};
      }
    }
  }
  
  .react-datepicker__month {
    padding: 0 0 15px;
  }

  .react-datepicker__day--outside-month {
    color: rgba(57, 57, 57, .3);
    background: transparent;
    border-color: transparent;
  }

  .react-datepicker__day-names {
    padding: 20px 15px 5px;

    @media (max-width: ${({ theme }): string => theme.screen.sm}) {
      padding: 15px 15px 0;
    }
  }

  .react-datepicker__day-name {
    font-size: 90%;
    font-weight: 600;
    color: rgba(0, 0, 0, .54);
  }

  .react-datepicker__day-name,
  .react-datepicker__day,
  .react-datepicker__time-name {
    width: 4rem;
    line-height: 4rem;
    border: 1px solid transparent;

    @media (max-width: ${({ theme }): string => theme.screen.sm}) {
      width: 3.5rem;
      line-height: 3.5rem;
    }
  }

  .react-datepicker__day-name {
    line-height: 2.2rem;
  }

  .react-datepicker__day--today {
    &:not(.react-datepicker__day--selected) {
      border-color: ${({ theme }): string => theme.colors.indigo};
      border-radius: 0.3rem;
    }
  }

  .react-datepicker__day--selected {
    background-color: ${({ theme }): string => theme.colors.indigo};

    &:hover {
      background-color: ${({ theme }): string => darken(0.1, theme.colors.indigo)};
    }
  }

  .react-datepicker__day--keyboard-selected {
    color: rgba(0, 0, 0, .9);
    background-color: ${({ theme }): string => theme.colors.moonRaker};

    &:hover {
      background-color: ${({ theme }): string => darken(0.1, theme.colors.indigo)};
    }
  }
  
  .react-datepicker__day--today,
  .react-datepicker__month-text--today,
  .react-datepicker__quarter-text--today,
  .react-datepicker__year-text--today {
    font-weight: 400;
  }

  .react-datepicker__current-month {
    font-size: 1.9rem;
    padding-bottom: 20px;
    border-bottom: 1px solid ${({ theme }): string => theme.colors.mercury};

    @media (max-width: ${({ theme }): string => theme.screen.sm}) {
      padding-bottom: 15px;
    }
  }

  .react-datepicker__today-button {
    background: none;
    padding: 0;
    border: none;
  }

  .react-datepicker__current-month,
  .react-datepicker__day-name {
    text-transform: capitalize;
  }

  .${POSTED_DAY_CLASSNAME} {
    &:not(.react-datepicker__day--selected) {
      color: ${({ theme }): string => theme.colors.emerald};

      &.react-datepicker__day--outside-month {
        color: ${({ theme }): string => transparentize(0.7, theme.colors.emerald)};
      }
    }
  }
  
  .${UNPOSTED_DAY_CLASSNAME} {
    &:not(.react-datepicker__day--selected) {
      color: ${({ theme }): string => theme.colors.indigo};

      &.react-datepicker__day--outside-month {
        color: ${({ theme }): string => transparentize(0.7, theme.colors.indigo)};
      }
    }
  }
`

const TodayButton = styled.div`
  cursor: pointer;
  height: 50px;
  line-height: 50px;
  background-color: ${({ theme }): string => theme.colors.indigo};
  color: ${({ theme }): string => theme.colors.white};
  font-weight: 600;
  user-select: none;
  border-radius: 0 0 5px 5px;
  transition: background-color .3s;

  &:hover {
    background-color: ${({ theme }): string => darken(0.1, theme.colors.indigo)};
  }

  @media (max-width: ${({ theme }): string => theme.screen.sm}) {
    height: 45px;
    line-height: 45px;
  }
`

const TodayButtonArrow = styled.div`
  position: relative;
  display: inline-block;
  vertical-align: middle;
  width: 7px;
  height: 7px;
  border-color: ${({ theme }): string => theme.colors.white};
  border-style: solid;
  border-width: 1px 1px 0 0;
  margin-left: 10px;
  transform: rotate(45deg);

  &:after {
    content: "";
    width: 11px;
    height: 1px;
    background-color: ${({ theme }): string => theme.colors.white};
    position: absolute;
    top: 5px;
    right: 0;
    transform: rotate(-45deg) translateX(2px);
  }
`

const Calendar = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const daySummaries = useSelector(TimelineStatsSlice.selectDaySummaries)
  const activeDateStr = useSelector(TimelineSlice.selectActiveDate)
  const activeDate = toDate(activeDateStr)
  const activeJsDate = new Date(activeDate.year, activeDate.month - 1, activeDate.day)
  const currentDay = useSelector(TimelineSlice.selectCurrentDay)
  const [isOpened, setIsOpened] = useState(false)

  const jumpToToday = () => {
    history.push(ROUTE_PATH.TIMELINE.path.replace(":date?", TODAY_DATE_PARAM))
  }
  return (
    <Wrapper>
      <DatePicker
        fixedHeight
        locale={getDatepickerLocale()}
        calendarClassName="tl-calendar-popup"
        formatWeekDay={(nameOfDay: string): string => nameOfDay.substr(0, 3)}
        selected={activeJsDate}
        open={isOpened}
        onChange={(date): void => {
          if (!date || Array.isArray(date)) {
            return
          }
          const timelineDate = convertDateToTimelineDate(date)

          if (currentDay === timelineDate) {
            jumpToToday()
          } else {
            history.push(ROUTE_PATH.TIMELINE.path.replace(
              ":date?",
              convertTimelineDateToTimelinePathDate(timelineDate),
            ))
          }
          dispatch(TimelineSlice.setActiveDate(timelineDate))
          setIsOpened(false)
        }}
        popperPlacement="bottom-end"
        popperModifiers={{
          flip: {
            behavior: ["bottom"],
          },
        }}
        customInput={React.createElement(CustomCalendarInput)}
        onInputClick = {() => setIsOpened(!isOpened)}
        onClickOutside={() => setIsOpened(false)}
        dayClassName={(date): string | null => {
          const daySummary = daySummaries[convertDateToTimelineDate(date)]
          const totalPosted = computeTotalPostedSecs(daySummary?.timePostedToSiteList)
          const totalUnposted = daySummary?.totalUnposted?.seconds ?? 0
          if (totalPosted > 0 && totalUnposted === 0) {
            return POSTED_DAY_CLASSNAME
          }
          if (totalUnposted > 0) {
            return UNPOSTED_DAY_CLASSNAME
          }
          return null
        }}
        todayButton={(
          <TodayButton onClick={jumpToToday}>
            <Trans id="label_jump_to_today" message="Jump to today"/>
            <TodayButtonArrow/>
          </TodayButton>
        )}
      />
    </Wrapper>
  )
}

export default Calendar
